import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { ApiError, bezugsSchluessel } from "@kundeninfo/api-client";
import type { ApiClient, Artikel, Position, PositionFelder, Positionsbezug, PositionsStand,
              Steuersatz } from "@kundeninfo/api-client";
import { formatDezimal, formatMenge } from "../format.js";
import { renderMarkdown } from "../markdown.js";
import { MarkdownHinweis } from "./MarkdownHinweis.js";
import { Auswahl, Chip, Eingabe, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import type { EingabeProps } from "@kundeninfo/ui/me-design";
import "./PositionsEditor.css";
import { Feld, Icon, Modal, ModalFuss, darfAbbrechen, useBestaetigung, useTelefon }
  from "@kundeninfo/ui";
import { KiKnopf, Suchfeld, useBeimVerlassen, LoeschKnopf } from "./meHuellen.js";
// Lieferanten Strang E (Aufgabe 18)
import { AssignmentHint, KontierungFelder, sharedAssignmentLists } from "./KontierungFelder.js";
import type { KontierungWert } from "./KontierungFelder.js";
import { KONTIERUNG_TEXT } from "./kontierungTexts.js";
import { DELETE_ITEM_QUESTION } from "./documentQuestionTexts.js";

/**
 * Der Positions-Editor eines Vorgangs — EINE Komponente für Angebot UND Auftrag
 * (Paket 3b-1b, Aufgabe 4).
 *
 * Sie sitzt am Angebot (KundeAngebote.tsx), unverändert am Auftrag (KundeAuftraege.tsx) und
 * seit Paket 3b-2 ebenso am RECHNUNGS-ENTWURF (KundeRechnungen.tsx — an einer
 * festgeschriebenen Rechnung wird sie gar nicht erst eingehängt): Der einzige Unterschied ist
 * der Bezug (`bezug`), und den reicht sie an den Klienten durch. Ein zweiter, nachgebauter
 * Editor wäre die sicherste Art, dass dieselbe Zeile an zwei Bildschirmen Verschiedenes
 * bedeutet.
 *
 * Seit Migration 029 sitzt sie ausserdem an der ANGEBOTSVORLAGE
 * (`views/verwaltung/angebotsvorlagen.tsx` über `components/VorlageDetail.tsx`) — der
 * vierte Einsatzort, und der erste ohne Kunden.
 *
 * **Der Server rechnet, die Maske zeigt — AUSNAHMSLOS.** Zeilennettos, Zwischensummen und
 * Steuersummen kommen fertig aus der Antwort (`zeilennettos`, `zwischensummen`,
 * `steuersummen`) — dieselbe Rechnung, die später hinter dem PDF steht. Hier wird KEINE Zahl
 * gebildet, auch keine Zeilenzahl: Das Zeilennetto stand eine Runde lang als Nachbau von
 * `domain/itemRules` in dieser Datei (weil `web` `domain` nicht importieren darf) und ist mit
 * Ruling P3B1B-I ersatzlos entfallen. Es kaufte nichts ein — gerechnet wurde ohnehin auf dem
 * SERVERSTAND, nicht auf dem Entwurf, die Spalte sprang also erst nach dem Speichern — und
 * kostete eine zweite Rundungswahrheit.
 *
 * **Jede Antwort trägt die frische `version`.** Positionen hängen an der optimistischen
 * Sperre des VORGANGS: Jeder Schreibweg schickt die Version und bekommt die neue zurück. Wer
 * eine davon nicht übernimmt, bekommt beim nächsten Klick 409 — und ab da bei jedem weiteren.
 * Deshalb läuft jede Antwort durch `uebernehmen()`, und jede 409 lädt neu statt zu meckern.
 *
 * Eigener CSS-Block `pe__` in einer EIGENEN Datei (PositionsEditor.css), mit keiner Ansicht
 * geteilt — Kollisions-Lehre 01.09.
 */

/** Nur die Klientenmethoden, die der Editor wirklich braucht — so lässt er sich im Test mit
 *  einer Handvoll vi.fn zeichnen (Muster der bestehenden Reiter-Tests). */
export type PositionsEditorClient = Pick<ApiClient,
  "listItems" | "createItem" | "updateItem" | "deleteItem" | "reorderItems" | "resetItem"
  | "searchProducts" | "listActiveTaxRates">;

/**
 * Die KONTIERUNG je Position (Plan „Rollen am Geschäftspartner und Lieferantenverträge“, Aufgabe 18): Auftrags- und
 * Rechnungsdialog setzen sie für JEDE Rolle, die die Positionen bearbeitet (P10, Bedenken 504); Angebot und Vorlage
 * setzen sie nicht — dort erscheinen keine Kontierungsfelder. Am Auftrag schreibt eine Wahl über den Positionsweg
 * (wie der Steuersatz), am Rechnungsentwurf sofort über den eigenen Kontierungsweg (er dreht die Belegversion nicht;
 * gefragt wird erst an der festgeschriebenen Rechnung, `InvoiceItemAssignment` — K2 aus Review 18). `gesperrt` zeigt die Felder lesend; an der Rechnung ausserdem nach der DATEV-Übergabe
 * (`assignmentLocked` im Positionsstand).
 */
export type PositionAssignment = {
  client: Pick<ApiClient, "getBookingAccounts" | "getCostCentres" | "setInvoiceItemAssignment">;
  companyId: string;
  art: "ORDER" | "INVOICE";
  gesperrt: boolean;
};

export type PositionsEditorProps = {
  client: PositionsEditorClient;
  /**
   * WORAN die Positionen hängen: ein Vorgang eines Kunden (`offer`/`order`/`invoice`) oder
   * eine ANGEBOTSVORLAGE (`template`, Migration 029).
   *
   * EINE Eigenschaft und nicht drei: Eine Vorlage hat keinen Kunden, und ein `kundeId`, das
   * dort leer bliebe, wäre die Sorte Feld, die beim nächsten Lesen für ein Versehen gehalten
   * wird. Der Typ kommt aus dem Klienten (`Positionsbezug`) und ist hier nicht nachgebaut —
   * als die Rechnung dazukam, brauchte diese Datei deshalb keine Zeile Änderung, und als
   * die Vorlage dazukam, ebenso wenig: Der Editor reicht den Bezug nur durch, die Familie
   * entscheidet der Server.
   */
  bezug: Positionsbezug;
  /** Das Unternehmen des Vorgangs — die Artikelsuche zeigt NUR dessen Artikelstamm. Ein
   *  firmenfremder Artikel wäre serverseitig ohnehin ein Feldfehler. */
  companyId: string;
  /** Die zuletzt bekannte Version des VORGANGS. Ändert der Elternteil den Kopf (und damit
   *  die Version), reicht er sie hier herein — sonst liefe der nächste Positionsschritt in
   *  eine 409. */
  version: number;
  /** Wird nach JEDER Antwort gerufen: die frische Version und der ganze Stand (aus dem der
   *  Elternteil `netAmount` und „hat Positionen?" liest). */
  onVersionChange: (version: number, stand: PositionsStand) => void;
  /**
   * Die umgebende Maske sperrt (ME-Etappe 3, Aufgabe 4, Fixrunde 1): solange ihr
   * Formularzustand nicht „bereit“ ist — ein Speichern läuft oder ein fremder Stand wartet
   * auf „Neu laden“. Dann schreibt der Editor nichts (sonst liefe er in seine eigene 409)
   * und seine Knöpfe sind zu; die Zelleneingaben sperrt das Formular selbst (readOnly).
   */
  gesperrt?: boolean | undefined;
  /** Die Währung des VORGANGS (`currency`, ISO-Code) für die Anzeige in der Zahlenzeile
   *  (P15). Eine Position trägt keine eigene Währung; weggelassen gilt „EUR“ (die Vorlage
   *  führt keine). */
  waehrung?: string | undefined;
  /** Die Kontierung je Position (Lieferanten Strang E) — fehlt sie, gibt es keine Kontierungsfelder. */
  kontierung?: PositionAssignment | undefined;
};

/** Die Gliederungsebenen — dieselbe Grenze wie `ITEM_LEVEL_MAX` in der Domäne (0..3). Als
 *  AUSWAHL, nicht als Zahlenfeld: Eine getippte 7 wäre ein Feldfehler statt einer Ebene. */
const EBENEN = [0, 1, 2, 3];

// ---------------------------------------------------------------------------
// Einleitungs- und Ausleitungstext mit Platzhalter-Vorschau
// ---------------------------------------------------------------------------

/** Ein Token ist `#{` gefolgt von einem beliebigen Namen ohne `}`, dann `}` — derselbe
 *  Ausdruck wie `PLATZHALTER_RE` in domain/masterdataRules. */
const PLATZHALTER_RE = /#\{([^}]*)\}/g;

/**
 * Die LOKALE Mini-Ersetzung für die Vorschau: bekannte Namen werden durch ihren Wert
 * ersetzt, alle übrigen Tokens bleiben UNVERÄNDERT stehen.
 *
 * Bewusst nicht `platzhalterAufloesen` aus der Domäne — `web` darf `domain` nicht
 * importieren (check-boundaries), und ein Nachbau der Namensliste wäre eine zweite Wahrheit:
 * Die Namen kommen deshalb aus `GET /api/platzhalter`, die Werte aus dem, was der Reiter
 * ohnehin geladen hat. Was der Reiter nicht kennt (z. B. eine Anschrift), bleibt als Token
 * sichtbar — besser eine erkennbare Lücke als ein stillschweigend leerer Satz.
 *
 * GENAU EIN Durchlauf über die Tokens des Ausgangstextes: Ein eingesetzter Wert, der selbst
 * ein Token enthält, wird nicht erneut aufgelöst (keine Rekursion, keine Endlosersetzung) —
 * dieselbe Zusage wie in der Domäne. `String.replace` mit globalem Ausdruck leistet das von
 * sich aus; die Ersetzungen werden nicht noch einmal abgesucht.
 *
 * Verbindlich ist diese Vorschau nicht: Fürs PDF löst die geteilte Render-Engine auf.
 */
export function ersetzePlatzhalter(text: string, werte: Record<string, string>): string {
  return text.replace(PLATZHALTER_RE, (treffer, name: string) =>
    Object.prototype.hasOwnProperty.call(werte, name) ? werte[name]! : treffer);
}

/**
 * Die Laufzeit-Platzhalter für die BILDSCHIRM-Vorschau der Freitextfelder (Migration 031:
 * vier Dauern, jede als Zahl PLUS Einheit — vorher zwei nackte Monatszahlen).
 *
 * Sie muss dieselben Worte zeigen wie das PDF — sonst sieht der Bearbeiter „24 Monate" und
 * der Kunde „2 Jahre". Die verbindliche Formatierung steht in `domain`
 * (`dauerText`/`laufzeitPlatzhalter`, von der Engine benutzt); `packages/web` darf `domain`
 * nicht importieren (check-boundaries), also steht sie hier ein zweites Mal — klein genug
 * dafür. Es gibt KEINEN Test, der diese Fassung wörtlich gegen `dauerText` hält (der
 * bräuchte den verbotenen Import); `kundeAngebote.test.tsx` prüft nur INDIREKT, indem es
 * die erwartete Wortform („2 Jahre") in der gerenderten Vorschau erwartet — eine
 * abweichende zweite Fassung fiele dort auf, ein stillschweigendes Auseinanderlaufen der
 * REGEL selbst (neue Einheit, neue Pluralform) nicht zwingend (Fund M5 der
 * Abschlussprüfung).
 *
 * Die Argumente sind ROHTEXTE aus dem Formular-Entwurf, keine vorkonvertierten Zahlen: Ein
 * geleertes Feld erzeugt so von selbst keinen Eintrag, ohne dass jeder Aufrufer erst
 * `ganzzahlOderNull` bemühen müsste — ein fehlender oder halber Wert lässt den Platzhalter
 * in der Vorschau stehen, statt als Leerstelle zu verschwinden. Genau daran sieht der
 * Bearbeiter, dass die Laufzeit noch zu ergänzen ist.
 *
 * Es sind ZWÖLF Namen: je Grösse der ganze Satz, `….wert` und `….einheit` — die beiden
 * Teile reisen einzeln mit, falls ein künftiger Beleg sie getrennt braucht.
 */
export function laufzeitVorschauWerte(felder: {
  termValue: string; termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string; minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string; renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string; noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
}): Record<string, string> {
  const werte: Record<string, string> = {};

  /** „Monat"/„Monate"/„Jahr"/„Jahre" — passend zur Zahl (wie `einheitWort` in domain). */
  function einheit(zahl: number, art: "MONTHS" | "YEARS"): string {
    if (art === "MONTHS") return zahl === 1 ? "Monat" : "Monate";
    return zahl === 1 ? "Jahr" : "Jahre";
  }

  function dreifach(name: string, roh: string, art: "MONTHS" | "YEARS",
                    ganz?: (text: string) => string): void {
    const kern = roh.trim();
    if (kern === "") return;
    const zahl = Number(kern);
    if (!Number.isInteger(zahl) || zahl < 0) return;
    const wortform = einheit(zahl, art);
    const text = `${String(zahl)} ${wortform}`;
    werte[name] = ganz === undefined ? text : ganz(text);
    werte[`${name}.wert`] = String(zahl);
    werte[`${name}.einheit`] = wortform;
  }

  dreifach("laufzeit.vertragslaufzeit", felder.termValue, felder.termUnit);
  dreifach("laufzeit.mindestlaufzeit", felder.minimumTermValue, felder.minimumTermUnit);
  dreifach("laufzeit.verlaengerung", felder.renewalPeriodValue, felder.renewalPeriodUnit);
  dreifach("laufzeit.kuendigungsfrist", felder.noticePeriodValue, felder.noticePeriodUnit,
    (text) => `${text} ${felder.noticeReference === "TERM_END"
      ? "zum Ende der Laufzeit" : "zum Ende des Kalenderjahres"}`);
  return werte;
}

export type PlatzhalterTextfeldProps = {
  id: string;
  label: string;
  /** Der ROHTEXT mit seinen `#{…}`-Tokens — genau das, was gespeichert wird. */
  wert: string;
  onChange: (wert: string) => void;
  /** Die bekannten Namen aus `GET /api/platzhalter` (ohne die #{}-Klammern) — als Legende
   *  unter dem Feld, damit niemand raten muss, was es überhaupt gibt. */
  variablen: string[];
  /** Die im Reiter geladenen Werte, nach Platzhaltername. Was fehlt, bleibt stehen. */
  werte: Record<string, string>;
  rows?: number;
  /** Das Feld wächst mit dem Text (`wachsend` an me-eingabe, Spec „Mitwachsende Textfelder“); `rows` bleibt Mindesthöhe. */
  wachsend?: boolean;
};

/**
 * Ein Textfeld für Einleitungs- bzw. Ausleitungstext: ROHTEXT oben, leichte Vorschau
 * darunter.
 *
 * Steht bewusst in DIESER Datei und nicht in der Ansicht: Es gehört zum selben
 * wiederverwendeten Editor (Angebot heute, Auftrag mit Aufgabe 5), trägt denselben
 * CSS-Block `pe__` und dieselbe Stildatei. In der Ansicht nachgebaut, gäbe es die Vorschau
 * zweimal — und die zweite wäre eines Tages die andere.
 *
 * **Die Vorschau rendert MARKDOWN, das Eingabefeld bleibt Rohtext** (Entscheid 05.09.2026).
 * Belegtexte werden geschrieben, um gesetzt zu werden — `**fett**` und eine Aufzählung
 * gehören im Beleg als solche gesetzt, und die Vorschau ist der Ort, an dem der Bearbeiter
 * das schon beim Tippen sieht. Gerendert wird mit dem VORHANDENEN `renderMarkdown`
 * (markdown.tsx, `kn-md`-Präfix, sanitisierend) — demselben, der Notizen und
 * Artikelbeschreibungen setzt; ein zweiter Renderer wäre ein zweiter Satz Sicherheitsregeln.
 * Insbesondere bleibt dessen Regel unangetastet, dass ein EXTERNES Bild als Link erscheint
 * und nicht als `<img>` (kein Ausspähkanal über die IP des Betrachters).
 *
 * Die Platzhalter werden VOR dem Rendern ersetzt: Der Bearbeiter soll den Satz sehen, der
 * entsteht — nicht `#{firma.name}` mitten im gesetzten Text. Verbindlich ist auch das
 * nicht; fürs PDF löst die geteilte Render-Engine auf (siehe `ersetzePlatzhalter`).
 */
export function PlatzhalterTextfeld(p: PlatzhalterTextfeldProps): ReactElement {
  const vorschau = ersetzePlatzhalter(p.wert, p.werte);
  return (
    <div className="pe__textblock">
      {/* `Feld typ="mehrzeilig"` (me-eingabe) seit me-design c9c0c08: Der Anfangswert
          behält seine Zeilenumbrüche (Befund 2) — vorher ein rohes <textarea> (Übergang). */}
      <Feld id={p.id} label={p.label} typ="mehrzeilig" bearbeitbar zeilen={p.rows ?? 3}
        wachsend={p.wachsend === true} wert={p.wert} onChange={p.onChange} />
      {/* Bei leerem Text KEINE Vorschau: sonst steht der Seitenbalken allein als
          verwaiste senkrechte Linie über der Legende (Befund Dirk 05.09.).

          Ein `<div>` und kein `<p>` mehr: renderMarkdown liefert Blöcke (Absätze, Listen,
          Tabellen), und die dürfen in einem Absatz gar nicht stehen. `role="group"` trägt
          dabei die Beschriftung — an einem blossen `<div>` würde ein aria-label von den
          meisten Vorlesern gar nicht angesagt. */}
      {vorschau.trim() !== "" && (
        <div className="pe__vorschau" role="group" aria-label={`${p.label} — Vorschau`}>
          {renderMarkdown(vorschau)}
        </div>
      )}
      {/* Zwei Fussnoten als Paar: erst die Sprache, in der hier geschrieben wird, dann
          die Wörter, die sie kennt. Der Markdown-Satz kommt aus dem gemeinsamen Baustein
          (MarkdownHinweis.tsx) — dieses eine Feld bedient Angebot, Auftrag UND Rechnung,
          also spart es allein schon drei Abschriften. */}
      <MarkdownHinweis />
      {p.variablen.length > 0 && (
        <p className="pe__legende">
          Platzhalter: {p.variablen.map((v) => `#{${v}}`).join(" · ")}
        </p>
      )}
    </div>
  );
}

const ART_LABEL: Record<Position["kind"], string> = {
  ITEM: "Position",
  SUBTOTAL: "Zwischensumme",
  TEXT: "Textzeile",
};

// ---------------------------------------------------------------------------
// Zahlen — Anzeige und Eingabe
// ---------------------------------------------------------------------------

/*
 * Die Zahlenformatierer stehen seit der Fixrunde 1 zu Aufgabe 7 in `format.ts`
 * (`formatDezimal`, `formatMenge`): Dieselben zwei `Intl`-Einstellungen standen wortgleich
 * hier, im Rechnungsdetail und in der Vorlagensicht — drei Gelegenheiten, dass derselbe
 * Betrag an drei Bildschirmen verschieden aussieht.
 */

/** Dasselbe für einen Betrag, den die Antwort GAR NICHT führt: Eine Textzeile hat kein
 *  Zeilennetto, und „0,00 €" wäre dort eine Behauptung. Dann bleibt die Zelle leer. */
function formatBetragOderLeer(wert: string | undefined): string {
  return wert === undefined ? "" : formatDezimal(wert);
}

/** Deutsche Kommazahl mitverstehen: „2.500,50" wie „2500.50" (Muster aus KundeAngebote). */
function zahlAus(wert: string): number {
  const normalisiert = wert.trim().replace(/\./g, "").replace(",", ".");
  if (normalisiert === "") return 0;
  const zahl = Number(normalisiert);
  return Number.isFinite(zahl) ? zahl : Number.NaN;
}

// ---------------------------------------------------------------------------
// Der Bearbeitungsstand einer Zeile
// ---------------------------------------------------------------------------

/** Die Werte EINER Zeile im Formular. Alle Zahlen als Zeichenkette, damit ein halb
 *  getippter Wert („1,") das Feld nicht zurücksetzt.
 *
 * `customerOrderLine` und `billingProbability` (Paket Teilrechnungen) gehören zur
 * POSITION, nicht zur Zwischensumme oder Textzeile — genau wie Menge und Einzelpreis
 * stehen sie hier als TEXT, die Umsetzung in Zahl macht `felderAus`. */
type ZeilenEntwurf = {
  /** Die Artikelnummer (Migration 040) — Text, leer heisst „keine". */
  articleNumber: string;
  name: string;
  description: string;
  unit: string;
  quantity: string;
  netUnitPrice: string;
  customerOrderLine: string;
  billingProbability: string;
};

function entwurfAus(item: Position): ZeilenEntwurf {
  return {
    articleNumber: item.articleNumber ?? "",
    name: item.name,
    description: item.description ?? "",
    unit: item.unit ?? "",
    quantity: formatMenge(item.quantity),
    netUnitPrice: formatDezimal(item.netUnitPrice),
    customerOrderLine: item.customerOrderLine ?? "",
    billingProbability: String(item.billingProbability),
  };
}

function entwuerfeAus(items: Position[]): Record<string, ZeilenEntwurf> {
  const alle: Record<string, ZeilenEntwurf> = {};
  for (const item of items) alle[item.id] = entwurfAus(item);
  return alle;
}

/** Die zehn Schreibfelder einer Zeile aus dem bestehenden Satz plus den Formularwerten. Die
 *  Stelle in der Folge (`sortIndex`) gehört NICHT dazu — sie ändert nur `reorderItems`. */
function felderAus(item: Position, entwurf: ZeilenEntwurf): PositionFelder {
  const istPosition = item.kind === "ITEM";
  return {
    kind: item.kind,
    level: item.level,
    productId: item.productId,
    name: entwurf.name.trim(),
    // Getrimmt wie der Name — und zwar in BEIDEN Zweigen. Wurde nur über den Leerraum
    // entschieden („leer oder nicht") und der ungetrimmte Wert geschickt, löste ein einzelnes
    // eingetipptes Leerzeichen hinter dem Text einen Schreibweg aus, den der Server danach
    // wegnormalisierte (Fund G6).
    description: entwurf.description.trim() === "" ? null : entwurf.description.trim(),
    // SUBTOTAL und TEXT tragen weder Einheit noch Zahlen noch Steuersatz — die Domäne weist
    // alles andere als Feldfehler ab, und die Maske bietet die Felder dort gar nicht erst an.
    unit: !istPosition || entwurf.unit.trim() === "" ? null : entwurf.unit.trim(),
    quantity: istPosition ? zahlAus(entwurf.quantity) : 0,
    netUnitPrice: istPosition ? zahlAus(entwurf.netUnitPrice) : 0,
    taxRateId: istPosition ? item.taxRateId : null,
    pageBreakBefore: item.pageBreakBefore,
    // Beide gehören zur POSITION, nicht zur Zwischensumme oder Textzeile. An der Rechnung
    // gibt es für beide Felder keine Eingabe (siehe `zeile()`) — der Entwurf trägt dort
    // unverändert den geladenen Wert weiter, und genau der geht hier mit, statt eines
    // `undefined`: Ein Antwortschema mit `additionalProperties: false` verlangt jedes
    // Feld auf BEIDEN Seiten (Global Constraint).
    customerOrderLine: !istPosition || entwurf.customerOrderLine.trim() === ""
      ? null : entwurf.customerOrderLine.trim(),
    billingProbability: istPosition
      ? Math.round(zahlAus(entwurf.billingProbability)) : 100,
    // Die Artikelnummer gehört zur POSITION (Entscheid 110) und geht IMMER mit — getrimmt,
    // leer als `null`. Fehlte sie, liesse der Server die gespeicherte stehen; das ist der
    // Weg für Oberflächen von vor Migration 040, nicht für diese.
    articleNumber: !istPosition || entwurf.articleNumber.trim() === ""
      ? null : entwurf.articleNumber.trim(),
  };
}

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. Feldfehler
 *  (422) werden angehängt — sie sagen genau, welche Spalte der Zeile nicht durchgeht. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (!(fehler instanceof ApiError)) return standard;
  const felder = (fehler.fields ?? []).map((f) => f.message).join(" ");
  const kern = fehler.message.trim() === "" ? standard : fehler.message;
  return felder === "" ? kern : `${kern} ${felder}`;
}

const KONFLIKT_HINWEIS =
  "Dieser Vorgang wurde zwischenzeitlich geändert. Die Positionen sind neu geladen — "
  + "bitte den Schritt wiederholen.";

/** Die Rückmeldung, wenn an einer gesperrten Maske doch jemand eine Zeile ändern will
 *  (Aufgabe 8, Re-Review A4): Vorher schluckte der Riegel den Versuch still. */
const GESPERRT_HINWEIS =
  "Die Positionen sind gerade gesperrt — die Maske speichert, oder der Vorgang wurde "
  + "inzwischen geändert (oben „Neu laden“).";

/** Welche Art von Zeile ein Einfügepunkt gerade anbietet (null = zu). */
type Einfuegewahl = { stelle: number; artikelSuche: boolean };

/** Wie ein Schreibweg ausging: geschrieben, von jemand anderem überholt (neu geladen) oder
 *  abgelehnt (Meldung steht). */
type Ausgang = "ok" | "konflikt" | "fehler";

/**
 * Die Position im Bearbeitungsdialog am TELEFON (Entscheid 42): der Entwurf ALLER Felder der
 * Zeile — auch der Sofort-Felder (Steuersatz, Ebene, Umbruch), die in der Zeile ohne
 * Zwischenstand schreiben. Gespeichert wird erst mit „Speichern“, in einem Schritt.
 */
type Bearbeitung = {
  item: Position; nummer: number; entwurf: ZeilenEntwurf;
  taxRateId: string | null; level: number; pageBreakBefore: boolean;
  fehler: string | null;
};

function bearbeitungAus(item: Position, nummer: number): Bearbeitung {
  return { item, nummer, entwurf: entwurfAus(item), taxRateId: item.taxRateId,
           level: item.level, pageBreakBefore: item.pageBreakBefore, fehler: null };
}

/** Die Schreibfelder des Dialogs: die Zeile aus dem Entwurf plus die Sofort-Felder. */
function felderDerBearbeitung(b: Bearbeitung): PositionFelder {
  return { ...felderAus(b.item, b.entwurf),
           taxRateId: b.item.kind === "ITEM" ? b.taxRateId : null,
           level: b.level, pageBreakBefore: b.pageBreakBefore };
}

/** Steht im Dialog etwas anderes als an der gespeicherten Zeile? (Rückfrage beim Abbrechen.) */
function bearbeitungGeaendert(b: Bearbeitung): boolean {
  const neu = felderDerBearbeitung(b);
  const alt = felderAus(b.item, entwurfAus(b.item));
  return (Object.keys(neu) as (keyof PositionFelder)[]).some((k) => neu[k] !== alt[k]);
}

/** Zebra-Kennzeichnung eines Zeilenträger-Kindes: nur ITEM-Zeilen zählen mit. */
function zebraAttr(item: Position): "aus" | undefined {
  return item.kind === "ITEM" ? undefined : "aus";
}

/**
 * Eine Zelleneingabe des Editors (ME-Etappe 4, P5): `me-eingabe` ohne sichtbare
 * Beschriftung — den Namen („Menge Zeile 2“) trägt `aria-label`, das Element spiegelt ihn ins
 * innere Feld. Kontrolliert über `wert`; gespeichert wird beim VERLASSEN (`blur` am Host),
 * nicht je Taste. Zahlen bleiben Text, solange getippt wird („1,“ ist ein Zwischenstand) —
 * gelesen und gerundet wird erst in `felderAus` bzw. auf dem Server.
 */
function ZellFeld(p: {
  name: string; wert: string; onText: (text: string) => void; onVerlassen: () => void;
  art?: "zahl" | "ganzzahl" | undefined; einheit?: string | undefined;
  platzhalter?: string | undefined; mehrzeilig?: boolean | undefined;
  gesperrt: boolean; className?: string | undefined;
}): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  useBeimVerlassen(host, p.onVerlassen);
  const optional: Partial<EingabeProps> = {};
  // `art="dezimal"` (me-design 5d54229): Textfeld, rechtsbündig, tabellarische Ziffern, kein
  // Drehknopf; `inputMode` der App gewinnt (Ganzzahl: numerisch).
  if (p.art !== undefined) optional.art = "dezimal";
  if (p.art === "ganzzahl") optional.inputMode = "numeric";
  if (p.einheit !== undefined) optional.einheit = p.einheit;
  if (p.platzhalter !== undefined) optional.platzhalter = p.platzhalter;
  // Die Beschreibung wächst mit dem Inhalt aus dem Element (`wachsend`, me-design 5bc1446,
  // Befund P15-2): leer zwei Zeilen, beim Tippen höher, nie ein Rollbalken im Feld.
  if (p.mehrzeilig === true) { optional.mehrzeilig = true; optional.zeilen = 2; optional.wachsend = true; }
  if (p.gesperrt) optional.gesperrt = true;
  const klassen = ["pe__feld", p.className ?? ""].filter((k) => k !== "").join(" ");
  return (
    <Eingabe ref={host} aria-label={p.name} wert={p.wert} className={klassen} {...optional}
      onGeaendert={(d) => { if (!p.gesperrt) p.onText(d.wert); }} />
  );
}

/** Ein leiser Knopf des Editors (Pfeile, Einfügen) — Vorgabe `leise`, damit eine Zeile mit
 *  sieben Wegen nicht aus sieben Kästen besteht. */
function PeKnopf(p: {
  art?: "leise" | "sekundaer" | undefined; name?: string | undefined;
  deaktiviert?: boolean | undefined; ausgeklappt?: boolean | undefined;
  className?: string | undefined; onClick: () => void; children: ReactNode;
}): ReactElement {
  return (
    <KiKnopf art={p.art ?? "leise"} className={`pe__knopf${p.className === undefined ? "" : ` ${p.className}`}`}
      name={p.name}
      deaktiviert={p.deaktiviert} ausgeklappt={p.ausgeklappt} onClick={p.onClick}>
      {p.children}
    </KiKnopf>
  );
}

export function PositionsEditor(p: PositionsEditorProps): ReactElement {
  const { client, bezug, companyId } = p;
  const waehrung = p.waehrung ?? "EUR";

  // Die Abhängigkeit ist der INHALT des Bezugs, nicht das Objekt: Das Literal im JSX ist bei
  // jedem Zeichnen ein neues, und ein Effekt an ihm lädt nach jedem Zeichnen neu. Gebildet
  // wird er beim TYP (`bezugsSchluessel` in @kundeninfo/api-client): Die Zahlplan-Tafel
  // braucht denselben Schlüssel, und eine zweite Abschrift derselben Regel liefe beim
  // nächsten Bezugsfall auseinander.
  const schluessel = bezugsSchluessel(bezug);

  const [stand, setStand] = useState<PositionsStand | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, ZeilenEntwurf>>({});
  const [saetze, setSaetze] = useState<Steuersatz[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [schreibt, setBeschaeftigt] = useState(false);
  /** Ein eigener Schreibweg läuft — ODER die umgebende Maske sperrt (`gesperrt`). */
  const beschaeftigt = schreibt || p.gesperrt === true;
  const [einfuegen, setEinfuegen] = useState<Einfuegewahl | null>(null);
  const [suchtext, setSuchtext] = useState("");
  const [treffer, setTreffer] = useState<Artikel[] | null>(null);
  const [gezogen, setGezogen] = useState<string | null>(null);
  /** Am Telefon (Entscheid 42) stehen die Zeilen nur lesend; bearbeitet wird im Dialog. */
  const telefon = useTelefon();
  const [bearbeitung, setBearbeitung] = useState<Bearbeitung | null>(null);

  /** Die Version, mit der der NÄCHSTE Schreibweg anfragt. Als Ref, nicht als Zustand: Sie
   *  wird mitten in einem Handler gelesen, unmittelbar nachdem ein anderer Schritt sie
   *  gesetzt hat — ein Zustandswert wäre dort noch der alte des Renderdurchlaufs. */
  const version = useRef(p.version);
  /** Der Rückruf in einer Ref, damit `laden` stabil bleibt: Läge er in den Abhängigkeiten
   *  des Lade-Effekts, zöge jede neue Funktionsidentität des Elternteils ein erneutes Laden
   *  nach sich — und weil das Laden wieder meldet, liefe es endlos. */
  const meldeVersion = useRef(p.onVersionChange);
  useEffect(() => { meldeVersion.current = p.onVersionChange; }, [p.onVersionChange]);
  useEffect(() => { version.current = p.version; }, [p.version]);

  /** Der aktuelle Bezug, ausserhalb der Abhängigkeitsliste von `laden` erreichbar (Muster
   *  `vorlageIdRef` in `views/verwaltung/angebotsvorlagen.tsx`): Das Objektliteral, das ein
   *  Aufrufer im JSX übergibt, ist bei jedem Zeichnen ein neues — läge es selbst in der
   *  Abhängigkeitsliste, bekäme `laden` bei jedem Zeichnen eine neue Identität, und der
   *  Lade-Effekt darunter liefe endlos nach. */
  const bezugRef = useRef(bezug);
  bezugRef.current = bezug;

  const uebernehmen = useCallback((neu: PositionsStand): void => {
    setStand(neu);
    setEntwuerfe(entwuerfeAus(neu.items));
    version.current = neu.version;
    meldeVersion.current(neu.version, neu);
  }, []);

  const laden = useCallback(async (): Promise<void> => {
    try {
      uebernehmen(await client.listItems(bezugRef.current));
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Positionen konnten nicht geladen werden."));
    }
  }, [client, schluessel, uebernehmen]);

  useEffect(() => { void laden(); }, [laden]);

  // Die Steuersätze für die Auswahlspalte. Scheitert das, bleibt der Editor bedienbar — nur
  // die Auswahl ist dann leer (die bestehende Zuordnung einer Zeile bleibt trotzdem stehen,
  // siehe satzOptionen unten).
  useEffect(() => {
    let ab = false;
    client.listActiveTaxRates()
      .then(({ items }) => { if (!ab) setSaetze(items); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  /**
   * Der gemeinsame Rahmen JEDES Schreibwegs: Version hinein, frischen Stand heraus.
   *
   * 409 heisst nicht „Fehler", sondern „jemand anders war schneller": neu laden und sagen,
   * dass der Schritt zu wiederholen ist. Alles andere (422 mit Feldfehlern, 404, 500) bleibt
   * als Meldung stehen, ohne den Stand anzufassen.
   */
  async function schreibe(
    lauf: (version: number) => Promise<PositionsStand>, standard: string,
    /** Wohin eine Ablehnung geht — Vorgabe: die Meldung über dem Editor; der Dialog am
     *  Telefon (Entscheid 42) zeigt sie bei sich, sonst stünde sie hinter dem Schleier. */
    fehlerAn: (meldung: string) => void = setFehler,
  ): Promise<Ausgang> {
    // Die Maske sperrt — kein Schreibweg, aber eine Rückmeldung statt Stille.
    if (p.gesperrt === true) {
      if (fehlerAn === setFehler) setHinweis(GESPERRT_HINWEIS); else fehlerAn(GESPERRT_HINWEIS);
      return "fehler";
    }
    setBeschaeftigt(true);
    setHinweis(null);
    try {
      uebernehmen(await lauf(version.current));
      setFehler(null);
      return "ok";
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setHinweis(KONFLIKT_HINWEIS);
        await laden();
        return "konflikt";
      }
      fehlerAn(alsMeldung(e, standard));
      return "fehler";
    } finally {
      setBeschaeftigt(false);
    }
  }

  const items = stand?.items ?? [];
  /** Die Zelleneingaben sperren mit der Maske (readOnly, der Fokus bleibt) — NICHT während
   *  eines eigenen Schreibwegs: Wer nach dem Verlassen eines Feldes schon im nächsten tippt,
   *  soll weitertippen können (wie bisher mit den nativen Feldern). */
  const zellenGesperrt = p.gesperrt === true;

  /**
   * Die Sperre der beiden Auswahlen (Steuersatz, Ebene) bei `gesperrt`: seit ME-Etappe 4 P4b
   * `Auswahl kompakt gesperrt` (Entscheid 33/94) — das Element hält den Fokus, klappt nicht
   * auf und fängt die Tasten ab. Hier bleibt nur die Rückmeldung, WARUM: Ein Klick oder eine
   * Taste (ausser Tab/Escape) an der gesperrten Auswahl nennt den Grund. Was trotzdem
   * durchkommt (Touch-Auswahl am Telefon), fängt der Riegel in `schreibe` ab.
   */
  const sperrHinweis = p.gesperrt === true ? {
    onMouseDown: () => setHinweis(GESPERRT_HINWEIS),
    // Der gesperrte Schalter schaltet beim Klick nicht und meldet nichts — der Grund kommt
    // von hier (vorher meldete ihn das Kästchen über den Riegel in `schreibe`).
    onClick: () => setHinweis(GESPERRT_HINWEIS),
    onKeyDown: (e: { key: string }) => {
      if (e.key === "Tab" || e.key === "Escape") return;
      setHinweis(GESPERRT_HINWEIS);
    },
  } : {};

  /**
   * Die Optionen der Steuersatz-Spalte: die AKTIVEN Sätze — und, falls die Zeile auf eine
   * stillgelegte oder abgelaufene Zeitscheibe zeigt, zusätzlich genau diese.
   *
   * Ohne den zweiten Teil verlöre ein altes Angebot beim ersten Speichern still seinen
   * Steuersatz: Das Auswahlfeld stünde auf einem Wert, den es nicht anbietet, und der Browser
   * meldete den ersten Eintrag zurück. Die Auflösung zum Belegdatum macht weiterhin der
   * Server; hier geht es nur darum, den Bestand nicht zu überschreiben.
   */
  function satzOptionen(item: Position): { id: string; text: string }[] {
    const optionen = saetze.map((s) => ({ id: s.id, text: `${s.name} (${s.ratePercent} %)` }));
    if (item.taxRateId !== null && !optionen.some((o) => o.id === item.taxRateId)) {
      optionen.unshift({ id: item.taxRateId, text: "bisheriger Satz (nicht mehr gepflegt)" });
    }
    return optionen;
  }

  function setzeEntwurf(id: string, feld: keyof ZeilenEntwurf, wert: string): void {
    setEntwuerfe((alle) => {
      const vorher = alle[id];
      if (vorher === undefined) return alle;
      return { ...alle, [id]: { ...vorher, [feld]: wert } };
    });
  }

  /** Speichert eine Zeile, wenn sich an ihr etwas geändert hat — beim Verlassen des Feldes.
   *  Ein Speicherweg je Tastenanschlag wäre ein Schreibsturm auf den Vorgang, und jeder davon
   *  drehte die Version weiter. */
  async function zeileSichern(item: Position): Promise<void> {
    const entwurf = entwuerfe[item.id];
    if (entwurf === undefined) return;
    const felder = felderAus(item, entwurf);
    const unveraendert =
      felder.name === item.name && felder.description === item.description
      && felder.unit === item.unit && felder.quantity === item.quantity
      && felder.netUnitPrice === item.netUnitPrice
      && felder.customerOrderLine === item.customerOrderLine
      && felder.billingProbability === item.billingProbability
      && felder.articleNumber === item.articleNumber;
    if (unveraendert) return;
    await schreibe(
      (v) => client.updateItem(bezug, item.id, v, felder),
      "Die Zeile konnte nicht gespeichert werden.");
  }

  /** Sofort-Änderungen (Auswahlfeld, Schalter) — sie haben keinen Zwischenzustand, den ein
   *  Verlassen abschliessen müsste. */
  async function zeileAendern(item: Position, teil: Partial<PositionFelder> & Partial<KontierungWert>): Promise<void> {
    const entwurf = entwuerfe[item.id] ?? entwurfAus(item);
    await schreibe(
      (v) => client.updateItem(
        bezug, item.id, v, { ...felderAus(item, entwurf), ...teil }),
      "Die Zeile konnte nicht geändert werden.");
  }

  const { bestaetigen } = useBestaetigung();
  async function zeileLoeschen(item: Position, nummer: number): Promise<void> {
    // Erst die Rückfrage (Entscheid 684, Hausregel „Bestätigung vor Datenänderung“) — Einfügen bringt nur eine leere
    // Zeile zurück, kein Rückgängig. Der Knopf bleibt leise (Entscheid 43 gilt der Farbe).
    if (!(await bestaetigen(DELETE_ITEM_QUESTION(String(nummer), item.name)))) return;
    await schreibe(
      (v) => client.deleteItem(bezug, item.id, v),
      "Die Zeile konnte nicht gelöscht werden.");
  }

  async function zeileZuruecksetzen(item: Position): Promise<void> {
    // Rückfrage, weil es die eingetippten Werte der Zeile verwirft — die MENGE bleibt.
    if (!(await bestaetigen({
      titel: "Zeile zurücksetzen?",
      text: `„${item.name}" auf die Werte aus dem Artikelstamm zurücksetzen? `
        + "Artikelnummer, Name, Beschreibung, Einheit, Preis und Steuersatz werden neu geholt; "
        + "die Menge bleibt.",
      bestaetigenText: "Zurücksetzen",
    }))) return;
    await schreibe(
      (v) => client.resetItem(bezug, item.id, v),
      "Die Zeile konnte nicht zurückgesetzt werden.");
  }

  // -------------------------------------------------------------------------
  // Kontierung je Position (Lieferanten Strang E, Aufgabe 18)
  // -------------------------------------------------------------------------

  /** Eine Wahl in den Kontierungsfeldern: am Auftrag der Positionsweg (wie jede Sofort-Änderung), an der Rechnung der
   *  eigene Weg — nach Rückfrage, weil er sofort und am (auch festgeschriebenen) Beleg wirkt. */
  async function assignItem(item: Position, wert: KontierungWert): Promise<void> {
    const k = p.kontierung;
    if (k === undefined) return;
    if (k.art === "ORDER") { await zeileAendern(item, wert); return; }
    if (bezug.art !== "invoice" || stand === null) return;
    // Am RechnungsENTWURF wirkt die Wahl sofort wie jede Positionsänderung (K2 aus Review 18, Dirk 216c); gefragt wird
    // nur an der festgeschriebenen Rechnung (`InvoiceItemAssignment`).
    setHinweis(null);
    try {
      const neu = await k.client.setInvoiceItemAssignment(bezug.customerId, bezug.vorgangId, item.id,
        { version: item.version, ...wert });
      // Die Version der Rechnung bleibt; nur die eine Position ist neu.
      uebernehmen({ ...stand, items: stand.items.map((i) => (i.id === neu.id ? neu : i)) });
      setFehler(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === "VERSIONSKONFLIKT") {
        setHinweis(KONTIERUNG_TEXT.invoiceConflict);
        await laden();
        return;
      }
      setFehler(alsMeldung(e, KONTIERUNG_TEXT.invoiceFailed));
    }
  }

  // Die Listen der Kontierungsfelder je Editor geteilt (siehe `sharedAssignmentLists`) — neu nur mit einem anderen Klienten.
  const assignmentClient = p.kontierung?.client;
  const lists = useMemo(
    () => (assignmentClient === undefined ? null : sharedAssignmentLists(assignmentClient)), [assignmentClient]);

  /** Die Kontierungsfelder einer Position — nur an ITEM-Zeilen und nur mit `kontierung`. Der Hinweis auf die
   *  Folgestufe steht einmal unter der Liste, nicht je Zeile (`hint={false}`). */
  function assignmentFields(item: Position, nummer: number): ReactElement | null {
    const k = p.kontierung;
    if (k === undefined || lists === null || item.kind !== "ITEM") return null;
    return (
      <div className="pe__assignment" role="group" aria-label={KONTIERUNG_TEXT.row(nummer)}>
        <KontierungFelder point={k.art === "ORDER" ? "ORDER_ITEM" : "INVOICE_ITEM"} companyId={k.companyId}
          client={lists} hint={false}
          gesperrt={k.gesperrt || p.gesperrt === true || stand?.assignmentLocked === true}
          wert={{ bookingAccountId: item.bookingAccountId, costCentreId: item.costCentreId }}
          onWert={(w) => { void assignItem(item, w); }} />
      </div>
    );
  }

  /** Die Zeilenklasse für das Raster mit Kontierungszeile. */
  const assignedClass = (item: Position): string =>
    (p.kontierung !== undefined && item.kind === "ITEM" ? " pe__zeile--assigned" : "");

  // -------------------------------------------------------------------------
  // Bearbeiten am Telefon (Entscheid 42)
  // -------------------------------------------------------------------------

  function setzeBearbeitung(teil: Partial<Omit<Bearbeitung, "entwurf">>,
                            entwurf?: Partial<ZeilenEntwurf>): void {
    setBearbeitung((b) => (b === null ? b : {
      ...b, ...teil, entwurf: { ...b.entwurf, ...entwurf } }));
  }

  /** „Speichern“ im Dialog: EIN Schreibweg mit allen Feldern. Erfolg und 409 schliessen den
   *  Dialog (bei 409 ist der Stand neu geladen und der Hinweis steht über dem Editor, wie in
   *  der Zeile); eine Ablehnung bleibt im Dialog stehen. */
  async function bearbeitungSpeichern(): Promise<void> {
    const b = bearbeitung;
    if (b === null || schreibt) return;
    if (!bearbeitungGeaendert(b)) { setBearbeitung(null); return; }
    const ausgang = await schreibe(
      (v) => client.updateItem(bezug, b.item.id, v, felderDerBearbeitung(b)),
      "Die Zeile konnte nicht gespeichert werden.",
      (meldung) => setzeBearbeitung({ fehler: meldung }));
    if (ausgang !== "fehler") setBearbeitung(null);
  }

  /** Abbrechen, Kreuz und Escape: mit Rückfrage, wenn im Dialog etwas geändert ist. */
  async function bearbeitungAbbrechen(): Promise<void> {
    if (bearbeitung === null || schreibt) return;
    if (!(await darfAbbrechen(bearbeitungGeaendert(bearbeitung)))) return;
    setBearbeitung(null);
  }

  // -------------------------------------------------------------------------
  // Einfügen
  // -------------------------------------------------------------------------

  /** Die Ebene, auf der eine neue Zeile beginnt: die der Zeile darüber. Wer in einen
   *  eingerückten Block einfügt, meint fast immer denselben Block. */
  function ebeneAn(stelle: number): number {
    return stelle === 0 ? 0 : (items[stelle - 1]?.level ?? 0);
  }

  function leereFelder(kind: Position["kind"], stelle: number): PositionFelder {
    const istPosition = kind === "ITEM";
    return {
      kind,
      level: ebeneAn(stelle),
      productId: null,
      // SUBTOTAL darf namenlos sein, ITEM und TEXT nicht — ein leerer Name käme als
      // Feldfehler zurück, bevor die Zeile überhaupt auf dem Bildschirm steht.
      name: kind === "ITEM" ? "Neue Position" : kind === "TEXT" ? "Text" : "Zwischensumme",
      description: null,
      unit: istPosition ? "Stück" : null,
      quantity: istPosition ? 1 : 0,
      netUnitPrice: 0,
      taxRateId: istPosition ? (saetze[0]?.id ?? null) : null,
      pageBreakBefore: false,
      // Eine neue Zeile trägt noch keine Bestellposition und die volle
      // Abrechnungswahrscheinlichkeit (Vorgabe 100) — beide sind an Angebot und Auftrag
      // sofort bearbeitbar.
      customerOrderLine: null,
      billingProbability: 100,
      articleNumber: null,
    };
  }

  async function einfuegenAn(stelle: number, kind: Position["kind"]): Promise<void> {
    setEinfuegen(null);
    await schreibe(
      (v) => client.createItem(
        bezug, v, leereFelder(kind, stelle), stelle),
      "Die Zeile konnte nicht eingefügt werden.");
  }

  /** Der gewählte Artikel wird als SCHNAPPSCHUSS kopiert: Artikelnummer, Name, Beschreibung,
   *  Einheit, Preis und Steuersatz stehen danach an der Zeile. `productId` bleibt als HERKUNFT stehen — sie
   *  trägt später das Zurücksetzen; eine spätere Preisänderung im Stamm ändert das Angebot
   *  aber nicht von selbst. */
  async function artikelEinfuegen(stelle: number, artikel: Artikel): Promise<void> {
    setEinfuegen(null);
    setTreffer(null);
    setSuchtext("");
    await schreibe(
      (v) => client.createItem(bezug, v, {
        kind: "ITEM",
        level: ebeneAn(stelle),
        productId: artikel.id,
        name: artikel.name,
        description: artikel.description,
        unit: artikel.unit,
        quantity: 1,
        netUnitPrice: artikel.netUnitPrice,
        taxRateId: artikel.taxRateId,
        pageBreakBefore: false,
        customerOrderLine: null,
        billingProbability: 100,
        // Die Artikelnummer als KOPIE wie der Name (Entscheid 110): Sie friert an der
        // Position ein und lässt sich dort ändern.
        articleNumber: artikel.productNumber,
      }, stelle),
      "Der Artikel konnte nicht eingefügt werden.");
  }

  async function artikelSuchen(): Promise<void> {
    try {
      const { items: gefunden } = await client.searchProducts({
        companyIds: [companyId], suche: suchtext, limit: 20,
      });
      setTreffer(gefunden);
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Artikelsuche ist fehlgeschlagen."));
    }
  }

  // -------------------------------------------------------------------------
  // Umsortieren — zwei gleichwertige Wege
  // -------------------------------------------------------------------------

  /**
   * Der EINE Umsortier-Weg, den beide Bedienarten benutzen: Ziehen und die Pfeilknöpfe.
   *
   * Geschickt wird immer die KOMPLETTE Id-Folge, nicht ein Verschiebebefehl — der Server
   * nimmt genau die vorhandenen Kennungen, jede einmal; eine Teilliste wäre mehrdeutig.
   */
  async function verschiebe(von: number, nach: number): Promise<void> {
    const ids = items.map((i) => i.id);
    if (von < 0 || nach < 0 || von >= ids.length || nach >= ids.length || von === nach) return;
    const geordnet = [...ids];
    const [bewegt] = geordnet.splice(von, 1);
    geordnet.splice(nach, 0, bewegt!);
    await schreibe(
      (v) => client.reorderItems(bezug, v, geordnet),
      "Die Reihenfolge konnte nicht gespeichert werden.");
  }

  /** Die gezogene Zeile steht im Zustand, nicht im `dataTransfer`: Letzteres ist in jsdom
   *  nicht durchgängig vorhanden, und die Kennung wird ohnehin nur innerhalb dieser Tabelle
   *  gebraucht. `setData` wird trotzdem versucht — manche Browser starten sonst gar kein
   *  Ziehen. */
  function ziehenStart(item: Position, uebertrag: DataTransfer | null): void {
    setGezogen(item.id);
    uebertrag?.setData?.("text/plain", item.id);
  }

  async function ablegenAuf(ziel: Position): Promise<void> {
    const quelleId = gezogen;
    setGezogen(null);
    if (quelleId === null || quelleId === ziel.id) return;
    const ids = items.map((i) => i.id);
    await verschiebe(ids.indexOf(quelleId), ids.indexOf(ziel.id));
  }

  // -------------------------------------------------------------------------
  // Zeichnen
  // -------------------------------------------------------------------------

  function einfuegepunkt(stelle: number): ReactElement {
    const offen = einfuegen?.stelle === stelle;
    return (
      <div className="pe__einfuegen" data-zebra="aus" key={`einfuegen-${stelle}`}>
        {/* P15 (Dirk 17.10.2026): vorher ein nacktes „+“ in einer Zwischenzeile — jetzt ein
            erkennbarer, aber leiser Knopf mit Plus-Symbol und Wort. Der zugängliche Name
            enthält das sichtbare Wort („Position“) und unterscheidet die Einfügepunkte. */}
        <PeKnopf deaktiviert={beschaeftigt} ausgeklappt={offen} className="pe__plus"
          name={`Position an Stelle ${stelle + 1} einfügen`}
          onClick={() => {
            // Treffer und Suchtext gehören zu DEM Einfügepunkt, an dem gesucht wurde —
            // stehen sie beim nächsten offen, fügt ein unbedachter Klick den Artikel an
            // der falschen Stelle ein.
            setTreffer(null);
            setSuchtext("");
            setEinfuegen(offen ? null : { stelle, artikelSuche: false });
          }}>
          <span className="pe__plus-inhalt">
            <Icon name="plus" size={14} className="pe__plus-symbol" />
            Position
          </span>
        </PeKnopf>
        {offen && (
          <span className="pe__einfuegen-wahl">
            <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
              onClick={() => setEinfuegen({ stelle, artikelSuche: true })}>
              Artikel suchen
            </PeKnopf>
            <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
              onClick={() => { void einfuegenAn(stelle, "ITEM"); }}>
              Freie Position
            </PeKnopf>
            <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
              onClick={() => { void einfuegenAn(stelle, "SUBTOTAL"); }}>
              Zwischensumme
            </PeKnopf>
            <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
              onClick={() => { void einfuegenAn(stelle, "TEXT"); }}>
              Textzeile
            </PeKnopf>
          </span>
        )}
        {offen && einfuegen?.artikelSuche === true && (
          <span className="pe__suche">
            {/* Eingabetaste sucht — ein Suchfeld, das nur über den Knopf daneben geht,
                ist im Alltag ein Stolperstein. Kein <form>: Der Editor steht INNERHALB
                der Vorgangsmaske, und ein verschachteltes Formular ist ungültiges HTML.
                `onEingabetaste` fängt die Taste VOR dem Element ab (me-eingabe sendete
                sonst ein umschließendes Formular ab). */}
            <Suchfeld name="Artikel suchen" className="pe__suchfeld" wert={suchtext}
              onText={setSuchtext} onEingabetaste={() => { void artikelSuchen(); }} />
            <PeKnopf art="sekundaer" onClick={() => { void artikelSuchen(); }}>
              Suchen
            </PeKnopf>
            {treffer !== null && treffer.length === 0 && (
              <span className="pe__leer">Kein Artikel gefunden.</span>
            )}
            {treffer !== null && treffer.length > 0 && (
              <ul className="pe__treffer">
                {treffer.map((a) => (
                  <li key={a.id}>
                    <PeKnopf onClick={() => { void artikelEinfuegen(stelle, a); }}>
                      {a.productNumber} · {a.name}
                    </PeKnopf>
                  </li>
                ))}
              </ul>
            )}
          </span>
        )}
      </div>
    );
  }

  /**
   * Eine Zeile des Editors — seit P15 (Dirks Zielbild 17.10.2026) DREI Zeilen je Position:
   *
   * 1. die Zahlen: Menge, Einheit, Einzelpreis, Steuersatz, Währung und rechts das
   *    berechnete Netto;
   * 2. der Name, einzeilig, so breit wie möglich — rechts daneben Bestellposition und
   *    Abrechnungswahrscheinlichkeit;
   * 3. die Beschreibung, mehrzeilig, so breit wie der Name, wächst mit dem Inhalt — rechts
   *    daneben Ebene, Umbruch, Pfeile, Zurücksetzen und Löschen.
   *
   * Links steht die Randspalte mit Griff, Art und den Marken (Umbruch, Budget). Die Folge im
   * DOM ist die Lesefolge (Zeile für Zeile, links vor rechts) und damit die Tabfolge.
   * Textzeilen tragen Name und Beschreibung (keine Zahlenzeile), Zwischensummen den Namen
   * mit dem Betrag rechts daneben (eine vorhandene Beschreibung bleibt sichtbar).
   */
  function zeile(item: Position, index: number): ReactElement {
    if (telefon) return zeileLesend(item, index);
    const entwurf = entwuerfe[item.id] ?? entwurfAus(item);
    const istPosition = item.kind === "ITEM";
    const nummer = index + 1;
    const mitBeschreibung = item.kind !== "SUBTOTAL" || entwurf.description !== "";
    const betrag = formatBetragOderLeer(item.kind === "SUBTOTAL"
      ? stand?.zwischensummen[item.id]
      : stand?.zeilennettos[item.id]);
    return (
      // Ziehen startet NUR am Griff (nicht an der ganzen Zeile): Liegt `draggable` an einem
      // Vorfahren, startet ein Mausziehen im Eingabefeld das Ziehen der Zeile statt einer
      // Textmarkierung — man könnte in keinem Feld mehr Text markieren. Ablegen bleibt an
      // der ganzen Zeile, damit die Trefferfläche gross ist.
      <div key={item.id} role="group" aria-label={`Zeile ${nummer}: ${ART_LABEL[item.kind]}`}
        className={`pe__zeile pe__zeile--${item.kind.toLowerCase()}`
          + (gezogen === item.id ? " pe__zeile--zieht" : "") + assignedClass(item)}
        data-kind={item.kind} data-ebene={item.level} data-zebra={zebraAttr(item)}
        style={{ "--pe-ebene": item.level } as CSSProperties}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); void ablegenAuf(item); }}>

        <div className="pe__rand">
          <span className="pe__griff" role="button" tabIndex={-1}
            aria-label={`Zeile ${nummer} verschieben`}
            draggable
            onDragStart={(e) => ziehenStart(item, e.dataTransfer)}
            onDragEnd={() => setGezogen(null)}>⠿</span>
          <span className="pe__art">{ART_LABEL[item.kind]}</span>
          {item.pageBreakBefore && (
            <span className="pe__umbruch" title="Seitenumbruch vor dieser Zeile">⤓</span>
          )}
          {/* Die Abrechnungswahrscheinlichkeit ist LESEND an jedem Einsatzort sichtbar —
              bearbeitbar nur an Angebot und Auftrag (siehe `pe__seite`). Bei 100 % (der
              Regelfall) bleibt die Pille weg: Nur die Ausnahme ist eine Meldung wert. */}
          {item.billingProbability < 100 && (
            <Chip statusArt="warnung">Budget, {item.billingProbability} %</Chip>
          )}
        </div>

        {/* Zeile 1 — die Zahlen. Die kleinen Beschriftungen (`pe__mini`) erscheinen nur, wenn
            der Editor zu schmal für die Kopfzeile ist (Telefon); sonst trägt die Kopfzeile
            darüber die Spaltennamen. Den zugänglichen Namen trägt in jedem Fall das Feld. */}
        {item.kind !== "TEXT" && (
        <div className="pe__zahlen">
          {istPosition && (
            <>
              {/* Die Artikelnummer ganz links (Entscheid 110), so breit wie die Menge — an
                  allen drei Vorgangsarten bearbeitbar, auch an der Rechnung (Entwurf). */}
              <span className="pe__wert pe__wert--artnr">
                <span className="pe__mini" aria-hidden="true">Art.-Nr.</span>
                <ZellFeld name={`Artikelnummer Zeile ${nummer}`} wert={entwurf.articleNumber}
                  platzhalter="Art.-Nr." gesperrt={zellenGesperrt}
                  onText={(t) => setzeEntwurf(item.id, "articleNumber", t)}
                  onVerlassen={() => { void zeileSichern(item); }} />
              </span>
              <span className="pe__wert pe__wert--menge">
                <span className="pe__mini" aria-hidden="true">Menge</span>
                <ZellFeld name={`Menge Zeile ${nummer}`} wert={entwurf.quantity} art="zahl"
                  gesperrt={zellenGesperrt}
                  onText={(t) => setzeEntwurf(item.id, "quantity", t)}
                  onVerlassen={() => { void zeileSichern(item); }} />
              </span>
              <span className="pe__wert pe__wert--einheit">
                <span className="pe__mini" aria-hidden="true">Einheit</span>
                <ZellFeld name={`Einheit Zeile ${nummer}`} wert={entwurf.unit}
                  gesperrt={zellenGesperrt}
                  onText={(t) => setzeEntwurf(item.id, "unit", t)}
                  onVerlassen={() => { void zeileSichern(item); }} />
              </span>
              <span className="pe__wert pe__wert--preis">
                <span className="pe__mini" aria-hidden="true">Einzelpreis</span>
                <ZellFeld name={`Einzelpreis Zeile ${nummer}`} wert={entwurf.netUnitPrice}
                  art="zahl" gesperrt={zellenGesperrt}
                  onText={(t) => setzeEntwurf(item.id, "netUnitPrice", t)}
                  onVerlassen={() => { void zeileSichern(item); }} />
              </span>
              <span className="pe__wert pe__wert--steuer" {...sperrHinweis}>
                <span className="pe__mini" aria-hidden="true">Steuersatz</span>
                <Auswahl beschriftung={`Steuersatz Zeile ${nummer}`} kompakt
                  className="pe__auswahl"
                  gesperrt={p.gesperrt === true} wert={item.taxRateId ?? ""}
                  onGeaendert={(d) => { void zeileAendern(item,
                    { taxRateId: d.wert === "" ? null : d.wert }); }}>
                  <option value="">— ohne Steuer —</option>
                  {satzOptionen(item).map((o) => (
                    <option key={o.id} value={o.id}>{o.text}</option>
                  ))}
                </Auswahl>
              </span>
              {/* Die Währung ist die des VORGANGS (eine Position trägt keine eigene) —
                  deshalb Anzeige, kein Feld. */}
              <span className="pe__wert pe__wert--waehrung">
                <span className="pe__mini" aria-hidden="true">Währung</span>
                <span className="pe__waehrung">{waehrung}</span>
              </span>
            </>
          )}
          {/* Das Netto ist BERECHNET und kommt aus der Antwort — an der Zeile gespeichert
              (oder hier nachgerechnet) wäre es eine zweite Wahrheit. Fehlt der Eintrag, ist
              die Zeile eine, die keinen Betrag trägt (TEXT): dann steht dort nichts, nicht
              „0,00". */}
          <span className="pe__wert pe__wert--netto">
            {betrag !== "" && (
              <span className="pe__mini" aria-hidden="true">
                {item.kind === "SUBTOTAL" ? "Zwischensumme" : "Netto"}
              </span>
            )}
            <span className="pe__netto ki-num">{betrag}</span>
          </span>
        </div>
        )}

        {/* Zeile 2 — der Name, so breit wie möglich. */}
        <div className="pe__name">
          <ZellFeld name={`Bezeichnung Zeile ${nummer}`} wert={entwurf.name}
            gesperrt={zellenGesperrt}
            onText={(t) => setzeEntwurf(item.id, "name", t)}
            onVerlassen={() => { void zeileSichern(item); }} />
        </div>

        {/* Rechts neben dem Namen: Bestellposition des Kunden und Abrechnungswahrschein-
            lichkeit (Paket Teilrechnungen) — bearbeitbar NUR an Angebot und Auftrag
            (Ruling T1): An einer Rechnung ist die Zuordnung bereits geschehen (beim
            Rechnungsvorschlag) und ausdrücklich nicht mehr änderbar (YAGNI, Spec); dort
            steht die Bestellposition nur lesend — sie sagt, WOHER die Zeile kommt. */}
        {istPosition && (
        <div className="pe__bestellung">
          {bezug.art !== "invoice" && (
            <>
              <ZellFeld name={`Bestellposition des Kunden Zeile ${nummer}`}
                platzhalter="Bestellposition" className="pe__feld--bestellung"
                wert={entwurf.customerOrderLine} gesperrt={zellenGesperrt}
                onText={(t) => setzeEntwurf(item.id, "customerOrderLine", t)}
                onVerlassen={() => { void zeileSichern(item); }} />
              {/* Die Einheit „%“ im Feld sagt, was die Zahl ist (vorher ein `title` —
                  Hausregel Sofort-Popups statt title). */}
              <ZellFeld name={`Abrechnungswahrscheinlichkeit Zeile ${nummer}`}
                art="ganzzahl" einheit="%" className="pe__feld--prozent"
                wert={entwurf.billingProbability} gesperrt={zellenGesperrt}
                onText={(t) => setzeEntwurf(item.id, "billingProbability", t)}
                onVerlassen={() => { void zeileSichern(item); }} />
            </>
          )}
          {bezug.art === "invoice" && item.customerOrderLine !== null && (
            <span className="pe__bestellposition-lese">{item.customerOrderLine}</span>
          )}
        </div>
        )}

        {/* Zeile 3 — die Beschreibung: so breit wie der Name, mehrzeilig, wächst mit dem
            Inhalt (leer zwei Zeilen, kein Rollbalken im Feld). */}
        {mitBeschreibung && (
          <div className="pe__beschreibung">
            <ZellFeld name={`Beschreibung Zeile ${nummer}`} wert={entwurf.description}
              mehrzeilig gesperrt={zellenGesperrt} className="pe__feld--text"
              platzhalter="Beschreibung"
              onText={(t) => setzeEntwurf(item.id, "description", t)}
              onVerlassen={() => { void zeileSichern(item); }} />
          </div>
        )}

        <div className="pe__zeilen-aktionen">
          <span className="pe__auswahl pe__auswahl--ebene" {...sperrHinweis}>
            <Auswahl beschriftung={`Ebene Zeile ${nummer}`} kompakt
              gesperrt={p.gesperrt === true} wert={String(item.level)}
              onGeaendert={(d) => { void zeileAendern(item, { level: Number(d.wert) }); }}>
              {EBENEN.map((e) => <option key={e} value={e}>Ebene {e}</option>)}
            </Auswahl>
          </span>
          {/* `me-schalter` (P5), kontrolliert über `an`: Der Stand kommt vom SERVER —
              das Element schaltet beim Klick vor, die Hülle zieht es beim nächsten
              Zeichnen auf den Positionsstand zurück, bis die Antwort ihn setzt. */}
          <span className="pe__schalter" {...sperrHinweis}>
            <Schalter beschriftung="Umbruch" aria-label={`Seitenumbruch vor Zeile ${nummer}`}
              an={item.pageBreakBefore} gesperrt={p.gesperrt === true}
              onGeaendert={(d) => { void zeileAendern(item, { pageBreakBefore: d.an }); }} />
          </span>
          <span className="pe__knopfgruppe">
            {/* Der zweite, GLEICHWERTIGE Weg zum Umsortieren. Natives HTML5-Ziehen hat auf
                iOS-Safari keine Touch-Entsprechung — und das System ist eine PWA für iPhone
                und iPad. Ohne diese beiden Knöpfe liesse sich dort gar nichts verschieben,
                mit der Tastatur ebenso wenig. Beide Wege laufen durch `verschiebe`. */}
            <PeKnopf deaktiviert={beschaeftigt || index === 0}
              name={`Zeile ${nummer} nach oben`}
              onClick={() => { void verschiebe(index, index - 1); }}>
              ▲
            </PeKnopf>
            <PeKnopf deaktiviert={beschaeftigt || index === items.length - 1}
              name={`Zeile ${nummer} nach unten`}
              onClick={() => { void verschiebe(index, index + 1); }}>
              ▼
            </PeKnopf>
            {item.productId !== null && (
              <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
                name={`Zeile ${nummer} auf den Artikel zurücksetzen`}
                onClick={() => { void zeileZuruecksetzen(item); }}>
                Zurücksetzen
              </PeKnopf>
            )}
            {/* Leiser Papierkorb (Entscheid 43): leise in der Farbe; rot bleibt der Rückfrage, die
                seit Entscheid 684 vor dem Löschen steht (`zeileLoeschen`). */}
            <LoeschKnopf className="pe__knopf" deaktiviert={beschaeftigt}
              name={`Position ${nummer} löschen`}
              onClick={() => { void zeileLoeschen(item, nummer); }} />
          </span>
        </div>
        {assignmentFields(item, nummer)}
      </div>
    );
  }

  /** Der Name des Steuersatzes einer Zeile für die lesende Form. */
  function satzText(item: Position): string {
    if (item.taxRateId === null) return "ohne Steuer";
    return satzOptionen(item).find((o) => o.id === item.taxRateId)?.text ?? "";
  }

  /**
   * Die Zeile am TELEFON (Entscheid 42, Story „Tabelle › Bearbeitungstabelle“): nur lesend,
   * dieselben drei Zeilen wie in der Bearbeitungsform — Zahlen, Name, Beschreibung — und
   * rechts unten die Wege der Zeile: Pfeile (Umsortieren bleibt in der Zeile, natives Ziehen
   * gibt es auf iOS nicht), Zurücksetzen, „Bearbeiten“ (öffnet den Dialog) und der leise
   * Papierkorb (Entscheid 43).
   */
  function zeileLesend(item: Position, index: number): ReactElement {
    const istPosition = item.kind === "ITEM";
    const nummer = index + 1;
    const betrag = formatBetragOderLeer(item.kind === "SUBTOTAL"
      ? stand?.zwischensummen[item.id]
      : stand?.zeilennettos[item.id]);
    const zahlen = istPosition ? [
      item.articleNumber,
      `${formatMenge(item.quantity)}${item.unit === null ? "" : ` ${item.unit}`}`
        + ` × ${formatDezimal(item.netUnitPrice)} ${waehrung}`,
      satzText(item),
    ].filter((t): t is string => t !== null && t !== "") : [];
    return (
      <div key={item.id} role="group" aria-label={`Zeile ${nummer}: ${ART_LABEL[item.kind]}`}
        className={`pe__zeile pe__zeile--lesend pe__zeile--${item.kind.toLowerCase()}${assignedClass(item)}`}
        data-kind={item.kind} data-ebene={item.level} data-zebra={zebraAttr(item)}
        style={{ "--pe-ebene": item.level } as CSSProperties}>
        <div className="pe__rand">
          <span className="pe__art">{ART_LABEL[item.kind]}</span>
          {item.pageBreakBefore && (
            <span className="pe__umbruch" aria-label="Seitenumbruch vor dieser Zeile">⤓</span>
          )}
          {item.billingProbability < 100 && (
            <Chip statusArt="warnung">Budget, {item.billingProbability} %</Chip>
          )}
        </div>
        {item.kind !== "TEXT" && (
          <div className="pe__zahlen pe__zahlen--lesend">
            {zahlen.length > 0 && <span className="pe__lese-zahlen">{zahlen.join(" · ")}</span>}
            <span className="pe__netto pe__netto--lesend ki-num">{betrag}</span>
          </div>
        )}
        <div className="pe__name"><span className="pe__lese-name">{item.name}</span></div>
        {istPosition && item.customerOrderLine !== null && (
          <div className="pe__bestellung">
            <span className="pe__bestellposition-lese">{item.customerOrderLine}</span>
          </div>
        )}
        {item.description !== null && item.description !== "" && (
          <div className="pe__beschreibung">
            <p className="pe__lese-text">{item.description}</p>
          </div>
        )}
        <div className="pe__zeilen-aktionen">
          <span className="pe__knopfgruppe">
            <PeKnopf deaktiviert={beschaeftigt || index === 0}
              name={`Zeile ${nummer} nach oben`}
              onClick={() => { void verschiebe(index, index - 1); }}>
              ▲
            </PeKnopf>
            <PeKnopf deaktiviert={beschaeftigt || index === items.length - 1}
              name={`Zeile ${nummer} nach unten`}
              onClick={() => { void verschiebe(index, index + 1); }}>
              ▼
            </PeKnopf>
            {item.productId !== null && (
              <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
                name={`Zeile ${nummer} auf den Artikel zurücksetzen`}
                onClick={() => { void zeileZuruecksetzen(item); }}>
                Zurücksetzen
              </PeKnopf>
            )}
            <PeKnopf art="sekundaer" deaktiviert={beschaeftigt}
              name={`Position ${nummer} bearbeiten`}
              onClick={() => setBearbeitung(bearbeitungAus(item, nummer))}>
              Bearbeiten
            </PeKnopf>
            <LoeschKnopf className="pe__knopf" deaktiviert={beschaeftigt}
              name={`Position ${nummer} löschen`}
              onClick={() => { void zeileLoeschen(item, nummer); }} />
          </span>
        </div>
        {assignmentFields(item, nummer)}
      </div>
    );
  }

  /**
   * Der Bearbeitungsdialog am Telefon (Entscheid 42): strikt (`Modal`, am Telefon
   * vollflächig), genau zwei Ausgänge — Speichern und Abbrechen —, Abbrechen fragt bei
   * Änderungen nach (`darfAbbrechen`). Die Felder sind dieselben wie in der Zeile, hier mit
   * sichtbarer Beschriftung.
   */
  function bearbeitungsDialog(b: Bearbeitung): ReactElement {
    const { item, entwurf } = b;
    const istPosition = item.kind === "ITEM";
    const aus = schreibt || p.gesperrt === true;
    const text = (feld: keyof ZeilenEntwurf, beschriftung: string,
                  extra: Partial<EingabeProps> = {}): ReactElement => (
      <Eingabe beschriftung={beschriftung} wert={entwurf[feld]} className="pe__dialogfeld"
        {...(aus ? { gesperrt: true } : {})} {...extra}
        onGeaendert={(d) => setzeBearbeitung({}, { [feld]: d.wert })} />
    );
    return (
      <Modal titel={`Position ${b.nummer} bearbeiten`} breite="mittel"
        onAbbrechen={() => { void bearbeitungAbbrechen(); }} abbrechenGesperrt={schreibt}>
        <div className="pe__dialog">
          {b.fehler !== null && <Hinweis art="fehler">{b.fehler}</Hinweis>}
          {istPosition && text("articleNumber", "Artikelnummer")}
          {istPosition && text("quantity", "Menge", { art: "dezimal" })}
          {istPosition && text("unit", "Einheit")}
          {istPosition && text("netUnitPrice", "Einzelpreis",
            { art: "dezimal", einheit: waehrung })}
          {istPosition && (
            <Auswahl beschriftung="Steuersatz" wert={b.taxRateId ?? ""}
              {...(aus ? { gesperrt: true } : {})}
              onGeaendert={(d) => setzeBearbeitung({ taxRateId: d.wert === "" ? null : d.wert })}>
              <option value="">— ohne Steuer —</option>
              {satzOptionen(item).map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
            </Auswahl>
          )}
          {text("name", "Bezeichnung")}
          {istPosition && bezug.art !== "invoice" && text("customerOrderLine",
            "Bestellposition des Kunden")}
          {istPosition && bezug.art !== "invoice" && text("billingProbability",
            "Abrechnungswahrscheinlichkeit", { art: "dezimal", inputMode: "numeric", einheit: "%" })}
          {text("description", "Beschreibung", { mehrzeilig: true, zeilen: 2, wachsend: true })}
          <Auswahl beschriftung="Ebene" wert={String(b.level)}
            {...(aus ? { gesperrt: true } : {})}
            onGeaendert={(d) => setzeBearbeitung({ level: Number(d.wert) })}>
            {EBENEN.map((e) => <option key={e} value={e}>Ebene {e}</option>)}
          </Auswahl>
          <Schalter beschriftung="Seitenumbruch vor dieser Zeile" an={b.pageBreakBefore}
            {...(aus ? { gesperrt: true } : {})}
            onGeaendert={(d) => setzeBearbeitung({ pageBreakBefore: d.an })} />
        </div>
        <ModalFuss speichernText="Speichern" speichernGesperrt={aus}
          abbrechenGesperrt={schreibt}
          onSpeichern={() => { void bearbeitungSpeichern(); }}
          onAbbrechen={() => { void bearbeitungAbbrechen(); }} />
      </Modal>
    );
  }

  return (
    <section className="pe" aria-label="Positionen">
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}

      {stand === null && fehler === null && <p className="pe__leer">Lädt …</p>}

      {stand !== null && (
        <>
          {/* Seit P15 keine Tabelle mehr: Eine Position sind drei Zeilen (Zahlen, Name,
              Beschreibung), und die passen in kein gemeinsames Spaltenraster. Die Kopfzeile
              nennt die Spalten der Zahlenzeile; ist der Editor dafür zu schmal (Telefon),
              fällt sie weg und jedes Zahlenfeld trägt seine kleine Beschriftung selbst. */}
          {/* Zebra: me-design 73d07f0, Entscheid 95/MW3, Regel in tokens.css — gezählt werden nur
              die direkten Kinder ohne data-zebra="aus" (die ITEM-Zeilen). */}
          <div className="pe__liste" data-zebra="an">
            <div className="pe__kopf" aria-hidden="true" data-zebra="aus">
              <span className="pe__kopf-rand">Art</span>
              <span className="pe__zahlen pe__zahlen--kopf">
                <span className="pe__wert--artnr">Art.-Nr.</span>
                <span className="pe__wert--menge">Menge</span>
                <span className="pe__wert--einheit">Einheit</span>
                <span className="pe__wert--preis">Einzelpreis</span>
                <span className="pe__wert--steuer">Steuersatz</span>
                <span className="pe__wert--waehrung">Währung</span>
                <span className="pe__wert--netto">Netto</span>
              </span>
            </div>
            {einfuegepunkt(0)}
            {items.map((item, index) => (
              // Zeile UND ihr Einfügepunkt darunter gehören zusammen — ein Fragment mit
              // Schlüssel hält beide beim Umsortieren beieinander.
              <Fragment key={item.id}>
                {zeile(item, index)}
                {einfuegepunkt(index + 1)}
              </Fragment>
            ))}
          </div>

          {items.length === 0 && (
            <p className="pe__leer">Noch keine Positionen — über „Position" die erste einfügen.</p>
          )}
          {/* Lieferanten Strang E: der Hinweis der Kontierungsfelder EINMAL unter der Liste (Fix-Runde 1, W1). */}
          {p.kontierung !== undefined && items.some((i) => i.kind === "ITEM") && (
            <AssignmentHint className="pe__assignment-hint" />
          )}

          <dl className="pe__summen">
            {stand.steuersummen.jeSatz.map((f) => (
              <div className="pe__summe" key={f.taxRateId ?? "ohne"}>
                <dt>{f.name} ({f.ratePercent} %) auf {formatDezimal(f.netto)} €</dt>
                <dd className="ki-num">{formatDezimal(f.steuer)} €</dd>
              </div>
            ))}
            <div className="pe__summe">
              <dt>Gesamt netto</dt>
              <dd className="ki-num">{formatDezimal(stand.steuersummen.gesamtNetto)} €</dd>
            </div>
            <div className="pe__summe">
              <dt>Umsatzsteuer</dt>
              <dd className="ki-num">{formatDezimal(stand.steuersummen.gesamtSteuer)} €</dd>
            </div>
            <div className="pe__summe pe__summe--brutto">
              <dt>Gesamt brutto</dt>
              <dd className="ki-num">{formatDezimal(stand.steuersummen.gesamtBrutto)} €</dd>
            </div>
          </dl>
        </>
      )}
      {bearbeitung !== null && bearbeitungsDialog(bearbeitung)}
    </section>
  );
}
